import GameBreakerLogo from '@apparel/core/arts/logo/gamebreaker_white.png';
import { AuthService } from '@apparel/core/auth';
import { isApparel } from '@apparel/core/platform';
import { useService } from '@apparel/react/hooks/useService';
import { CartDropdown } from '@apparel/ui/cart';
import { Logo } from '@assets/Logo';
import { Color } from '@components/Color';
import { CurrentUser } from '@components/CurrentUser';
import { InlineFlex } from '@components/Layout/Blocks';
import { AutoCol } from '@components/Layout/Col';
import { useAuthRedux } from '@hooks/useAuthRedux';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { useReactRouter } from '@hooks/useRouter';
import { checkWholesalerDomain, getSubdomainData } from '@lib/utils';
import AppBar, { AppBarProps } from '@material-ui/core/AppBar';
import IconButton from '@material-ui/core/IconButton';
import Slide from '@material-ui/core/Slide';
import Toolbar from '@material-ui/core/Toolbar';
import Tooltip from '@material-ui/core/Tooltip';
import CloseIcon from '@material-ui/icons/Close';
import HelpIcon from '@material-ui/icons/Help';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import { dispatchWindowEvent } from '@packages/events/lib/dispatchEvent';
import { ApplicationContext } from '@packages/layout';
import { SidebarToggle } from '@packages/layout/lib/Sidebar/SidebarToggle';
import { HeaderMountPoint } from '@packages/layout/lib/UIShell/HeaderMountPoint';
import { NotificationBell } from '@packages/notifications/NotificationBell';
import { MountTo } from '@packages/portals';
import { createMemo } from '@packages/react';
import { IF } from '@packages/react/lib/if';
import { UserView } from '@packages/user';
import React, { useCallback, useContext, useEffect } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { route } from '../../../router';
import { getScripts } from '../loadScripts';
import { PAGE_HEADER_THEME, THEMES } from './theme';
import { _height } from './utils';

const pathArr = ['/customer-question'];

/**
 * @author: Dumitrana Alinus <alin.designstudio@gmail.com>
 * @class: Header
 * @date:  19.03.2019
 * @time:  00:13
 * @scope:
 */
export const PageHeader = createMemo<AppBarProps>(
    (props) => {
        const [state] = useContext(ApplicationContext);
        const { history } = useReactRouter();
        const [auth] = useAuthRedux();
        const location = useLocation();
        const auth$ = useService(AuthService);
        const visible = state.get('header').visibility;
        const wholesalerBySubdomain = getSubdomainData();
        const hostCheck = checkWholesalerDomain();
        const handleEffect = useCallback(() => {
            setTimeout(() => dispatchWindowEvent('resize'));
        }, []);
        const isMobile = useMediaQuery('mobile');
        const isTablet = useMediaQuery('max-960');
        const isDr = location?.pathname.includes('/designs/request/view/');
        const isSignUpPage = location?.pathname === '/sign-up' && !wholesalerBySubdomain?.id;
        const contactUrl = wholesalerBySubdomain?.contact_url || 'https://gamebreaker.com/contact-us';
        useEffect(() => {
            getScripts(hostCheck?.isGB);
        }, []);

        const checkPath = () => pathArr.indexOf(location?.pathname);

        const handleRestore = useCallback(async () => {
            await auth$.restoreBackup();
            setTimeout(() => {
                window.location.reload();
            }, 100);
        }, []);
        const handleHelp = useCallback(() => {
            history.push(route('Help.List'));
        }, []);

        useEffect(() => {
            if (typeof document !== 'undefined') {
                document.body.style.backgroundColor = state.getIn(['page', 'background']) || '#eee';
            }
        }, [state.getIn(['page', 'background'])]);

        useEffect(() => {
            handleEffect();
        }, [visible]);

        return (
            <MountTo to={HeaderMountPoint.POINT} name={'page-header'}>
                <Slide direction={'down'} in={visible} unmountOnExit onExited={handleEffect}>
                    <PageHeaderWithStyles
                        headerConfig={wholesalerBySubdomain?.header_config}
                        signup={isSignUpPage}
                        classes={{ root: 'header__holder' }}
                        elevation={state.getIn(['header', 'elevation'])}
                        {...props}
                    >
                        <Toolbar
                            style={{ minHeight: '55.5px' }}
                            classes={
                                isDr
                                    ? { root: 'header__centeredToolbar' }
                                    : { root: isSignUpPage ? 'header__toolbar signup-header' : 'header__toolbar' }
                            }
                        >
                            {isSignUpPage ? (
                                <>
                                    <a href={'/'} className={'signup-logo-link'}>
                                        <img
                                            className={'signup-logo'}
                                            src={wholesalerBySubdomain?.logo || GameBreakerLogo}
                                            alt={'GameBreaker'}
                                        />
                                    </a>
                                    <SignupHeaderActions>
                                        <Link className={'account-link'} to={'/sign-in'}>
                                            Already have an account?
                                        </Link>
                                        <a
                                            className={'contact'}
                                            href={contactUrl}
                                            target={'_blank'}
                                            rel={'noopener noreferrer'}
                                        >
                                            Contact Us
                                        </a>
                                    </SignupHeaderActions>
                                </>
                            ) : null}
                            {!isSignUpPage ? (
                            <>
                            <AutoCol
                                xs={12}
                                sm={isDr && isApparel() ? 4 : ''}
                                padding={!isMobile && !isTablet && '0 14px 0 0'}
                                maxWidth={isDr && isApparel() ? 330 : ''}
                            >
                                {IF(
                                    !isDr && auth && checkPath() < 0,
                                    <SidebarToggle
                                        color={wholesalerBySubdomain?.header_config?.textColor || THEMES.dark.color}
                                    />,
                                )}

                                {isDr ? (
                                    <div className={'header__brand'}>
                                        <Logo
                                            hideLetters={true}
                                            color={THEMES.dark.color}
                                            height={32}
                                            className={'header__brand__image'}
                                        />
                                    </div>
                                ) : (
                                    <a href={IF(checkPath() < 0, '/', '')} className={'header__brand'}>
                                        <Logo
                                            color={THEMES.dark.color}
                                            height={32}
                                            className={'header__brand__image'}
                                        />
                                    </a>
                                )}
                            </AutoCol>

                            {isDr && (isMobile || isTablet) ? (
                                <AutoCol xs justify={'flex-end'} maxWidth={isMobile && 138}></AutoCol>
                            ) : (
                                <AutoCol
                                    xs
                                    align={'center'}
                                    justify={'flex-end'}
                                    maxWidth={320}
                                    padding={'0 10px 0 0'}
                                >
                                    {IF(
                                        !isDr,
                                        <>
                                            {auth$.hasBackup() ? (
                                                <Tooltip
                                                    title={
                                                        "Looks like you've been logged as another account, feel free to comeback to your account whenever you want!"
                                                    }
                                                    arrow
                                                >
                                                    <IconButton onClick={handleRestore}>
                                                        <SupervisorAccountIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null}

                                            <UserView view={'customer'}>
                                                {!wholesalerBySubdomain?.id ? (
                                                    <InlineFlex m={!isMobile && !isTablet && '0 14px 0 0'}>
                                                        <Tooltip
                                                            title={'Frequently asked questions helping videos!'}
                                                            arrow
                                                        >
                                                            <IconButton onClick={handleHelp} color={'inherit'}>
                                                                <Color component={HelpIcon} color={'#8d8d8d'} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </InlineFlex>
                                                ) : null}
                                                <InlineFlex
                                                    m={!isMobile && !isTablet && '0 14px 0 0'}
                                                    className={'cart-holder'}
                                                >
                                                    {checkPath() < 0 ? <CartDropdown /> : null}
                                                </InlineFlex>
                                                <InlineFlex m={!isMobile && !isTablet && '0 14px 0 0'}>
                                                    {checkPath() < 0 ? <NotificationBell /> : null}
                                                </InlineFlex>
                                                {checkPath() < 0 ? <CurrentUser variant={'action'} /> : null}
                                            </UserView>

                                            <UserView view={'customer'} not>
                                                <InlineFlex
                                                    p={!isMobile && !isTablet ? '0 14px 0 0' : '0 15px 0 20px'}
                                                    m={'5px 0 0 0'}
                                                >
                                                    {checkPath() < 0 ? <NotificationBell /> : null}
                                                </InlineFlex>
                                                {checkPath() < 0 ? <CurrentUser variant={'action'} /> : null}
                                            </UserView>
                                        </>,
                                    )}
                                </AutoCol>
                            )}

                            {IF(
                                isDr,
                                <AutoCol xs align={'center'} justify={'flex-end'} maxWidth={50}>
                                    <IconButton className={'header__dr_button'} onClick={() => history.goBack()}>
                                        <CloseIcon />
                                    </IconButton>
                                </AutoCol>,
                            )}
                            </>
                            ) : null}
                        </Toolbar>
                    </PageHeaderWithStyles>
                </Slide>
            </MountTo>
        );
    },
    {
        displayName: 'PageHeader',
        defaultProps: {
            position: 'relative',
        },
    },
);
interface PageHeaderProps extends AppBarProps {
    headerConfig?: any;
    signup?: boolean;
}

const PageHeaderWithStyles = styled(AppBar)<PageHeaderProps>`
    && {
        display: block;
        min-height: ${_height()};
        background-color: ${(props) => props.headerConfig?.backgroundColor || THEMES.dark.background};
        padding-left: 8px;
        border-bottom: ${(props) =>
            props.signup ? '2px solid #E4002B' : `1px solid ${props.headerConfig?.borderColor || THEMES.dark.borderColor}`};

        & .header__toolbar {
            color: ${(props) => props.headerConfig?.textColor || THEMES.dark.color};
        }

        ${PAGE_HEADER_THEME.get('breakpoint')} {
            min-height: ${_height('mobile')};
        }

        .signup-header {
            width: 100%;
            justify-content: flex-start;
        }

        .signup-logo-link {
            display: inline-flex;
            align-items: center;
            flex: 0 0 auto;
            margin-left: 20px;
        }

        .signup-logo {
            width: 176px;
            height: 22px;
            max-width: 176px;
            object-fit: contain;
            object-position: left center;
            display: block;

            @media screen and (max-width: 768px) {
                width: 140px;
                max-width: 140px;
            }
        }

        .cart-holder {
            .MuiButtonBase-root {
                color: ${(props) => props.headerConfig?.textColor || THEMES.dark.color};
            }
        }

        .header {
            &__brand {
                margin-left: 8px;
                display: inline-flex;
                align-content: center;
                align-items: center;
                justify-content: center;

                &__image {
                    @media screen and (max-width: 767px) {
                        height: 26px !important;
                    }

                    @media screen and (max-width: 599px) {
                        height: 16px !important;
                    }
                }
            }

            &__toolbar {
                padding: 0;
                height: ${_height()};
                color: ${(props) => props.headerConfig?.textColor || '#fff'};
                @media screen and (max-width: 767px) {
                    height: ${_height('mobile')};
                    min-height: ${_height('mobile')};
                }
            }
            &__centeredToolbar {
                padding: 0;
                height: ${_height()};
                justify-content: space-between;
                color: ${(props) => props.headerConfig?.textColor || '#fff'};
                @media screen and (max-width: 767px) {
                    height: ${_height('mobile')};
                    min-height: ${_height('mobile')};
                }
            }
            &__dr_button {
                color: ${(props) => props.headerConfig?.textColor || '#fff'};
            }
        }
    }
`;

const SignupHeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 22px;
    margin-left: auto;
    padding-right: 16px;
    flex: 0 0 auto;

    .account-link {
        color: #fff;
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        text-decoration: none;
        white-space: nowrap;
        text-decoration: underline;
    }

    && .account-link:hover,
    && .account-link:focus {
        color: #fff;
        text-decoration: underline;
    }

    .contact {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        width: 120px;
        height: 32px;
        padding: 0;
        background: #e4002b;
        color: #fff;
        font-size: 12.5px;
        font-weight: 700;
        text-transform: uppercase;
        text-decoration: none;
        border-radius: 8px;
        white-space: nowrap;
    }

    && a.contact:hover,
    && a.contact:focus,
    && a.contact:active {
        background: #e4002b;
        color: #fff !important;
        text-decoration: none;
    }

    @media screen and (max-width: 768px) {
        .account-link {
            display: none;
        }
    }
`;
